/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Injectable, signal, WritableSignal} from '@angular/core';
import {defer, finalize, firstValueFrom, Observable, takeUntil, Subject} from 'rxjs';
import _ from 'lodash';
import moment from 'moment';
import 'moment-timezone';
import {SignalAdminResourceListState, SignalAdminResourceListStateFactory} from './signal-admin-resource-list-state';
import {
  SignalAdminResourceApi,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from './signal-admin-resource-api';
import {JOIN_INFO} from './join-data';
import {apiInstantFromPickerDateBoundary, networkDayFromPickerDate, pickerDateFromNetworkDay, savedValueToNetworkDay} from '../locale/network-date-picker-utils';
import {ALL_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {getCountryName} from '../directives/country-select/countries';
import {formatLocaleNumber} from '../locale/locale-number-format';
import {AppLocaleService} from '../locale/app-locale.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserProfileApiService} from '../../admin/user-profile/user-profile-api.service';
import {AdnModalLoadingService} from '../util/adn-modal-loading.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {ExceptionHandlerService} from '../../errors/exception-handler.service';
import {bulkApiErrorsFromResponse} from './bulk-action-errors';

export interface SignalResourceListBindingOptions {
  afterFill?: (items?: any[]) => void;
  afterBulkAction?: () => void;
  afterAction?: (items: any, action: any) => void;
  moreParams?: Record<string, any>;
  pageSize?: number;
  baseTable?: string;
  noInitQuery?: boolean;
  paginate?: boolean;
  loadPage?: (params: SignalAdminResourceListParams) => Observable<SignalAdminResourcePage<any>>;
  [key: string]: any;
}

/** Explicit adapter for the few complex list screens not yet using list state directly. */
export interface SignalResourceListController<T extends SignalAdminResourceModel = SignalAdminResourceModel> {
  listState?: SignalAdminResourceListState<T>;
  listStateRef?: WritableSignal<SignalAdminResourceListState<T> | null>;
  disposeList?: () => void;
  readOnly?: boolean;
  [key: string]: any;
}

/** Binds existing row/editor controls to signal state. No copied loading state or change-detection polling. */
@Injectable({providedIn: 'root'})
export class SignalResourceListBindingService {
  constructor(
    private readonly factory: SignalAdminResourceListStateFactory,
    private readonly appLocale: AppLocaleService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly networkProfile: LocalNetworkProfile,
    private readonly userProfileApi: UserProfileApiService,
    private readonly location: BrowserLocationService,
    private readonly modalLoading: AdnModalLoadingService,
    private readonly exceptionHandler: ExceptionHandlerService
  ) {}

  create<T extends SignalAdminResourceModel>(
    api: SignalAdminResourceApi<T>,
    listId: string,
    destroyRef: DestroyRef,
    config: SignalResourceListBindingOptions = {},
    initial: SignalResourceListController<T> = {}
  ): SignalResourceListController<T> {
    return this.bind(initial, api, listId, destroyRef, config);
  }

  bind<T extends SignalAdminResourceModel>(ctrl: SignalResourceListController<T>, api: SignalAdminResourceApi<T>, listId: string, destroyRef: DestroyRef,
    config: SignalResourceListBindingOptions = {}): SignalResourceListController<T> {
    ctrl.disposeList?.();
    const stopped = new Subject<void>();
    const localUserProfile = this.localUserProfile;
    const LocalNetworkProfile = this.networkProfile;
    const UserProfile = this.userProfileApi;
    const location = this.location;
    const networkTimezone = LocalNetworkProfile.get('timeZone') || 'utc';
    const networkDayShifter = {
      getNetworkTimezone: () => networkTimezone,
      getMomentInNetworkTimezone: (date) => moment(date).tz(networkTimezone)
    };
    config = {...config};
    config.moreParams = config.moreParams || {};
    config.getDefaultOrderByParam = config.getDefaultOrderByParam || (() => 'name');
    config.nullsAsOldestParams = config.nullsAsOldestParams || [];
    config.selectIn = config.selectIn || {};
    config.concise = config.concise !== false;
    const sortsNullsAsOldest = (param, numeric?) => numeric || config.nullsAsOldestParams.includes(param);
    const afterFill = [config.afterFill].filter(Boolean);
    let localUser = localUserProfile.get();
    let profile = localUser.profile;
    const defaultSort = config.getDefaultOrderByParam();
    const state = this.factory.create(config.loadPage ? {...api, list: config.loadPage} : api, {
      listId,
      baseTable: config.baseTable,
      defaultOrderBy: defaultSort.replace('DESC', ''),
      defaultAscending: !defaultSort.startsWith('DESC'),
      pageSize: config.pageSize,
      retainSelection: config.retainSelection,
      nullsAsOldestParams: config.nullsAsOldestParams,
      saveSortSettings: (settings) => {
        if (listId) {
          const previous = _.get(profile, ['adnLists', listId], {});
          _.set(profile, ['adnLists', listId], {...previous, ...settings});
        }
        saveUserProfile();
      },
      syncPageToUrl: !!config.paginate,
      requestParams: () => buildParams(),
      afterLoad: (items) => afterFill.forEach((fn) => fn(items)),
      onError: (error) => this.exceptionHandler.handle(error)
    }, destroyRef);
    const listStateRef = ctrl.listStateRef || signal<SignalAdminResourceListState<T> | null>(null);
    ctrl.listStateRef = listStateRef;
    if (!Object.getOwnPropertyDescriptor(ctrl, 'listState')?.get) {
      Object.defineProperty(ctrl, 'listState', {
        configurable: true,
        enumerable: true,
        get: () => listStateRef()
      });
    }
    listStateRef.set(state);
    const property = (name: string, value: WritableSignal<any>) => Object.defineProperty(ctrl, name, {
      configurable: true, enumerable: true, get: () => value(), set: (next) => value.set(next)
    });
    for (const [name, value] of Object.entries({
      items: state.items, totalCount: state.totalCount,
      currentPage: state.currentPage, include: state.include, showingInclude: state.include,
      orderByParam: state.orderBy, asc: state.ascending, nullsOrderPolicy: state.nullsOrderPolicy, searchTerm: state.searchTerm,
      searchTermCommitted: state.committedSearch, bulkSelection: state.selected,
      undoItems: state.undoItems, currentAction: state.undoAction, undoBulkSelection: state.undoBulkSelection
    })) property(name, value);
    Object.defineProperties(ctrl, {
      pageSize: {configurable: true, enumerable: true, get: () => state.pageSize},
      totalCountText: {configurable: true, get: () => formatLocaleNumber(state.totalCount(), undefined, this.appLocale.getLocaleId())},
      bulkSelectSize: {configurable: true, get: () => state.selectedCount()},
      bulkSelectState: {configurable: true, get: () => state.allSelected() ? 'all' : state.partiallySelected() ? 'partial' : 'empty'}
    });
    state.readOnly.set(!!ctrl.readOnly || !api);
    ctrl.setOrderByParam = (value) => state.orderBy.set(value);
    ctrl.setAsc = (value) => state.ascending.set(value);
    ctrl.setNullsOrderPolicy = (value?) => ctrl.nullsOrderPolicy = value;
    ctrl.setMoreParams = (value) => ctrl.moreParams = value;
    ctrl.addAfterFill = (fn) => afterFill.push(fn);
    const initDateFilters = function() {
      ctrl.startDateFilter = {dir: '>='};
      ctrl.endDateFilter = {dir: '<='};
    };

    const init = function() {
      const defaultOrderByParam = config.getDefaultOrderByParam();
      ctrl.setOrderByParam(defaultOrderByParam.replace("DESC", ""));
      ctrl.setAsc(defaultOrderByParam.indexOf("DESC") !== 0);
      ctrl.setMoreParams({...config.moreParams});
      ctrl.setNullsOrderPolicy();
      const pageNo = parseInt(_.get(location.queryParams, 'pageNo'));
      ctrl.currentPage = Number.isFinite(pageNo) && pageNo > 0 && config.paginate ? pageNo : 1;

      ctrl.include = 'ACTIVE';
      ctrl.showingInclude = ctrl.include;
      ctrl.selectIn = config.selectIn;

      if (!_.isArray(ctrl.items)) {
        ctrl.items = [];
      }

      initDateFilters();

      const listSettings = listId ? _.get(profile, 'adnLists.' + listId, {}) : 'name';
      if (listSettings.orderByParam) {
        ctrl.setOrderByParam(listSettings.orderByParam);
        ctrl.setAsc(_.isBoolean(listSettings.asc) ? listSettings.asc : true);
      }
      if (sortsNullsAsOldest(ctrl.orderByParam, listSettings.isNumeric)) {
        ctrl.setNullsOrderPolicy(ctrl.asc ? 'FIRST' : 'LAST');
      }
      if (listSettings.filterByKeyValue) {
        ctrl.filterByKeyValue = listSettings.filterByKeyValue;
      }
      if (listSettings.selectIn) {
        ctrl.selectIn = listSettings.selectIn;
        _.forEach(ctrl.selectIn, function(val, key) {
          if (_.isObject(val)) {
            ctrl.selectIn[key + 'Sum'] = _.sum(_.map(val, function(innerVal) {
              return innerVal === true ? 1 : 0;
            }));
            ctrl.selectIn[key + 'Selected'] = ctrl.selectIn[key + 'Sum'] > 0;
            ctrl[key + 'Working'] = _.filter(_.map(val, function(val, key) {
              return val === true ? key : '';
            }), function(val) {
              return val;
            });
          }
        });
      }
      if (listSettings.startDateFilter) {
        ctrl.startDateFilter = listSettings.startDateFilter;
        const startDay = savedValueToNetworkDay(listSettings.startDateFilter.startDate, networkTimezone);
        ctrl.startDateFilter.workingStartDate = startDay ? pickerDateFromNetworkDay(startDay, networkTimezone, 'start') : null;
      }
      if (listSettings.endDateFilter) {
        ctrl.endDateFilter = listSettings.endDateFilter;
        const endDay = savedValueToNetworkDay(listSettings.endDateFilter.endDate, networkTimezone);
        ctrl.endDateFilter.workingEndDate = endDay ? pickerDateFromNetworkDay(endDay, networkTimezone, 'start') : null;
      }
      if (listSettings.targetType) {
        ctrl.targetType = listSettings.targetType;
        ctrl.workingTargetType = listSettings.targetType || null;
      }
      if (listSettings.team) {
        ctrl.team = listSettings.team;
      }
      if (listSettings.order) {
        ctrl.order = listSettings.order;
      }
    };



    let joinParam: string;
    let whereFieldData: any;
    const joinType = null;
    const buildParams = () => {
      const leftJoinInfo = JOIN_INFO[ctrl.orderByParam];
      const params: any = {orderBy: _.get(leftJoinInfo, 'whereField') || ctrl.orderByParam.split("|")[0], concise: config.concise, orderByDirection: ctrl.asc ? 'ASCENDING' : 'DESCENDING', pageSize: ctrl.pageSize, page: ctrl.currentPage};
      params.locale = localUserProfile.get("locale");
      if (ctrl.nullsOrderPolicy) {
        params.nullsOrderPolicy = ctrl.nullsOrderPolicy;
      }
      if (!config.childCall && (!ctrl.selectIn || _.isEmpty(ctrl.selectIn))) {
        if (ctrl.filterByKeyValue) {
          const filterValues = ctrl.filterByKeyValue.split(":");
          if (filterValues.length === 2) {
            params.filterBy = filterValues[0];
            params.filterByValue = filterValues[1];
          }
        }

        if ((config.filterBy && config.filterByValue) || (ctrl.filterBy && ctrl.filterByValue)) {
          params.filterBy = ctrl.filterBy || config.filterBy;
          params.filterByValue = ctrl.filterByValue || config.filterByValue;
        }
        if ((config.filterBy && config.filterByLike) || (ctrl.filterBy && ctrl.filterByLike)) {
          params.filterBy = ctrl.filterBy || config.filterBy;
          params.filterByLike = ctrl.filterByLike || config.filterByLike;
        }
      }
      if (config.excludeIfMissing || ctrl.excludeIfMissing) {
        params.excludeIfMissing = ctrl.excludeIfMissing || config.excludeIfMissing;
      }
      params.includeActive = false;
      params.includeInactive = false;
      params.includeHidden = false;

      if (ctrl.searchTermCommitted) {
        params.filterBy = 'name;description;siteUrl;username';
        params.filterByLike = ctrl.searchTermCommitted;
      }

      params.joins = [];

      const joinInfo = joinParam ? JOIN_INFO[joinParam] : null;
      if (leftJoinInfo && (!joinInfo || joinInfo.table !== leftJoinInfo.table)) {
        params.joins.push('left join ' + leftJoinInfo.table + ' on ' + (config.baseTable || leftJoinInfo.table) + '.' + leftJoinInfo.field + '=' + leftJoinInfo.joinField);
      }

      if (config.onlyMine === true) {
        params.onlyMine = config.onlyMine;
      }

      params.where = [];
      if (config.where) {
        params.where = [config.where];
      } else {
        if (_.isArray(whereFieldData)) {
          _.forEach(whereFieldData, function(fd) {
            params.where.push(fd);
          });
        } else if (whereFieldData || joinType === 'LEFT') {
          const joinString = joinType === 'LEFT' ? 'left' : 'inner';
          const joinInfo = JOIN_INFO[joinParam];
          params.joins.push(joinString + ' join ' + joinInfo.table + ' on ' + config.baseTable + '.' + joinInfo.field + '=' + joinInfo.joinField);
          if (joinType === 'LEFT') {
            params.where.push(joinInfo.whereField + ' is null');
          } else {
            params.where.push(joinInfo.whereField + '=' + whereFieldData);
          }
          ctrl.joinMessage = joinInfo.translation + ': ' + (joinType === 'LEFT' ? 'orphan' : whereFieldData);
        } else {
          ctrl.joinMessage = '';
        }
      }

      if (_.get(ctrl.startDateFilter, 'startDate')) {
        // fixing saved settings that don't include the =
        const suffix = ctrl.startDateFilter.dir.indexOf("=") > -1 ? "" : "=";
        const boundary = ctrl.startDateFilter.dir.indexOf('>') > -1 ? 'start' : 'end';
        const startDate = apiInstantFromPickerDateBoundary(ctrl.startDateFilter.startDate, networkDayShifter, boundary);
        params.where.push(config.baseTable + ".startDate" + ctrl.startDateFilter.dir + suffix + startDate.toISOString());
      }
      if (_.get(ctrl.endDateFilter, 'endDate')) {
        // fixing saved settings that don't include the =
        const suffix = ctrl.endDateFilter.dir.indexOf("=") > -1 ? "" : "=";
        const boundary = ctrl.endDateFilter.dir.indexOf('>') > -1 ? 'start' : 'end';
        const endDate = apiInstantFromPickerDateBoundary(ctrl.endDateFilter.endDate, networkDayShifter, boundary);
        params.where.push(config.baseTable + ".endDate" + ctrl.endDateFilter.dir + suffix + endDate.toISOString());
      }

      if (ctrl.targetType) {
        params.targeting = _.get(ALL_TARGETING_TYPES, [ctrl.targetType, 'apiType'], "");
      }

      if (ctrl.team) {
        params.team = ctrl.team.id;
      }

      if (ctrl.order) {
        params.order = ctrl.order.id;
      }

      if (ctrl.selectIn) {
        // can't have both selectIn and filterBy work together.
        ctrl.filterByKeyValue = "";
        ctrl.filterBy = "";
        ctrl.filterByValue = "";
        _.forEach(ctrl.selectIn, function(valObj, key) {
          const values = [];
          _.forEach(valObj, function(val, key) {
            if (val === true) {
              values.push(key);
            }
          });
          if (values.length === 0) {
            return;
          }
          params.where.push((config.baseTable || (leftJoinInfo || {}).table) + '.' + key + " in " + values.join(","));
        });
      }

      if (ctrl.include) {
        params['include' + _.capitalize(ctrl.include)] = true;
      }

      params.joins = params.joins.join(";");
      params.where = params.where.join(";");

      if (config.id) {
        params.id = config.id.join(";");
      }
      if (ctrl.countryTeam) {
        params.teamCountry = ctrl.countryTeam;
      }
      // Explicit parent scope takes precedence over saved filter selections.
      return _.assign({}, params, ctrl.moreParams);
    };
    ctrl.makeQuery = () => state.load();
    ctrl.changePage = () => {
      if (config.paginate) location.setQueryParam('pageNo', ctrl.currentPage === 1 ? undefined : ctrl.currentPage);
      state.load();
    };
    ctrl.filterOnSelect = (_baseTable, clearOut) => {
      joinParam = clearOut ? null : ctrl.joinFilterBy;
      whereFieldData = ctrl.showOrphan && !clearOut
        ? ['lineItemId is null', 'creativeSetId is null']
        : ctrl['join' + joinParam]?.name;
      ctrl.savedFilterBy = joinParam;
      if (clearOut) ctrl.showOrphan = false;
      ctrl.orderFilterPopover = ctrl.orderFilterPopover2 = ctrl.orderFilterPopover3 = false;
      state.currentPage.set(1);
      state.load();
    };
    const saveUserProfile = function() {
      // Persist list settings to LocalStorage immediately so cleared filters
      // (e.g. team) are not restored if the user navigates away before the
      // UserProfile save callback — and so merge-based saves can see nulls.
      localUser = localUserProfile.safeSaveProfile('adnLists', profile.adnLists);
      profile = localUser.profile;
      firstValueFrom(UserProfile.saveProfile({
        id: localUser.userId,
        profile: _.pick(profile, ['adnLists'])
      }).pipe(takeUntil(stopped))).then(function(apiUser) {
        localUser = localUserProfile.safeSaveProfile('adnLists', apiUser.profile.adnLists);
        profile = localUser.profile;
      }).catch(() => undefined);
    };

    const setOrDeleteListSetting = function(key, value) {
      if (!listId) {
        return;
      }
      // Persist null rather than deleting the key. Profile saves use merge
      // (especially for local-only users like adnuntius@adnuntius.com), and
      // merge keeps destination keys that are missing from the source.
      if (_.isNil(value)) {
        _.set(profile, 'adnLists.' + listId + '.' + key, null);
        return;
      }
      _.set(profile, 'adnLists.' + listId + '.' + key, value);
    };

    ctrl.makeFilterQuery = function() {
      if (listId) {
        _.set(profile, 'adnLists.' + listId + '.filterByKeyValue', ctrl.filterByKeyValue);
      }
      state.currentPage.set(1);
      state.load();

      saveUserProfile();
    };

    ctrl.searchList = function(clearTerm) {
      if (clearTerm) {
        ctrl.searchTerm = "";
      }
      ctrl.searchTermCommitted = ctrl.searchTerm;
      state.currentPage.set(1);
      state.load();
      ctrl.undoItems = [];
    };

    ctrl.makeDateQuery = function(clearAll) {
      if (clearAll) {
        initDateFilters();
      } else {
        ctrl.datePopover = false;
      }
      ctrl.startDateFilter.startDate = ctrl.startDateFilter.workingStartDate;
      ctrl.endDateFilter.endDate = ctrl.endDateFilter.workingEndDate;
      state.currentPage.set(1);
      state.load();
      if (listId) {
        const startDateCopy = _.cloneDeep(ctrl.startDateFilter);
        startDateCopy.startDate = startDateCopy.startDate
          ? networkDayFromPickerDate(startDateCopy.startDate, networkDayShifter)
          : "";
        delete startDateCopy.workingStartDate;
        _.set(profile, 'adnLists.' + listId + '.startDateFilter', startDateCopy);
        const endDateCopy = _.cloneDeep(ctrl.endDateFilter);
        endDateCopy.endDate = endDateCopy.endDate
          ? networkDayFromPickerDate(endDateCopy.endDate, networkDayShifter)
          : "";
        delete endDateCopy.workingEndDate;
        _.set(profile, 'adnLists.' + listId + '.endDateFilter', endDateCopy);
      }
      saveUserProfile();
    };

    ctrl.makeSelectInQuery = function(key, value, maxOfKind) {
      if (value) {
        if (_.isArray(value)) {
          ctrl.selectIn[key] = {};
          _.forEach(value, function(val) {
            ctrl.selectIn[key][val] = true;
          });
        } else {
          ctrl.selectIn[key] = ctrl.selectIn[key] || {};
          ctrl.selectIn[key][value] = !ctrl.selectIn[key][value];
        }
      } else {
        _.forEach(_.keys(ctrl.selectIn[key]), function(k) {
          ctrl.selectIn[key][k] = false;
        });
        ctrl[key + 'Working'] = [];
      }
      if (!LocalNetworkProfile.hasObjectSource()) {
        // safety for line item source filter and the in-and-out nature of showing source depending on network status
        delete ctrl.selectIn.source;
        delete ctrl.selectIn.sourceSum;
      }
      ctrl.selectIn[key + 'Sum'] = _.sum(_.map(ctrl.selectIn[key], function(val) {
        return val === true ? 1 : 0;
      }));
      ctrl.selectIn[key + 'Selected'] = ctrl.selectIn[key + 'Sum'] > 0 && ctrl.selectIn[key + 'Sum'] < maxOfKind;
      state.currentPage.set(1);
      state.load();
      if (listId) {
        _.set(profile, 'adnLists.' + listId + '.selectIn', ctrl.selectIn);
        // can't have selectIn and filterBy stuff at the same time
        setOrDeleteListSetting('filterByKeyValue', null);
        setOrDeleteListSetting('filterBy', null);
        setOrDeleteListSetting('filterByValue', null);
      }
      saveUserProfile();
      ctrl[key + 'Popover'] = false;
    };

    ctrl.makeIncludeQuery = function() {
      state.currentPage.set(1);
      state.load();
      ctrl.undoItems = [];
    };

    ctrl.makeSortQuery = (param, numeric?) => state.sort({param, numeric}, {preventDefault: () => undefined} as Event);

    // Required to refresh the list data after the bulk action.
    ctrl.getTargetingName = function() {
      return _.get(ctrl, ['targetingTypes', ctrl.targetType, 'title']);
    };

    ctrl.makeTargetingQuery = function(clearOut) {
      if (clearOut) {
        ctrl.targetType = null;
        ctrl.workingTargetType = null;
      } else {
        ctrl.targetType = ctrl.workingTargetType;
      }
      setOrDeleteListSetting('targetType', ctrl.targetType);
      ctrl.targetingPopover = false;
      state.currentPage.set(1);
      state.load();

      saveUserProfile();
    };

    ctrl.makeRetailProductQuery = function(type, clearOut) {
      const basic = type;
      const working = 'working' + _.capitalize(type);
      const popover = type + 'Popover';

      if (clearOut) {
        ctrl[basic] = null;
        ctrl[working] = null;
      } else {
        ctrl[basic] = ctrl[working];
      }
      ctrl[popover] = false;
      state.currentPage.set(1);
      state.load();
    };

    // Required to refresh the list data after the bulk action.
    ctrl.getTeamName = function() {
      return _.get(ctrl, ['team', 'name']);
    };

    ctrl.getCountryName = function() {
      return getCountryName(_.get(ctrl, ['countryTeam']));
    };

    ctrl.makeCountryTeamQuery = function(clearOut) {
      if (clearOut) {
        delete ctrl.workingCountryTeam;
        delete ctrl.moreParams.countryTeam;
        delete ctrl.countryTeam;
      } else {
        ctrl.countryTeam = ctrl.workingCountryTeam;
      }
      setOrDeleteListSetting('countryTeam', ctrl.countryTeam);
      ctrl.countryTeamPopover = false;
      state.currentPage.set(1);
      state.load();
      saveUserProfile();
    };

    ctrl.makeTeamQuery = function(clearOut, andReturn) {
      if (clearOut) {
        delete ctrl.team;
        delete ctrl.moreParams.team;
        delete ctrl.workingTeam;
      } else {
        ctrl.team = ctrl.workingTeam;
        ctrl.makeOrderQuery(true, true);
      }
      setOrDeleteListSetting('team', ctrl.team);
      if (andReturn) {
        return;
      }
      ctrl.teamPopover = false;
      state.currentPage.set(1);
      state.load();
      saveUserProfile();
    };

    // Required to refresh the list data after the bulk action.
    ctrl.getOrderName = function() {
      return _.get(ctrl, ['order', 'name']);
    };

    ctrl.makeOrderQuery = function(clearOut, andReturn) {
      if (clearOut) {
        delete ctrl.order;
        delete ctrl.moreParams.order;
        delete ctrl.workingOrder;
      } else {
        ctrl.order = ctrl.workingOrder;
        ctrl.makeTeamQuery(true);
      }
      setOrDeleteListSetting('order', ctrl.order);
      if (andReturn) {
        return;
      }
      ctrl.orderPopover = false;
      state.currentPage.set(1);
      state.load();
      saveUserProfile();
    };

    ctrl.addItemToList = (item) => {
      state.items.update((items) => [...items, item]);
      state.totalCount.update((count) => count + 1);
    };

    ctrl.selectToggle = (item) => state.toggle(item);
    ctrl.selectAll = () => state.selectAll();
    ctrl.deselectAll = () => state.deselectAll();
    const bulkSave = (items, resource = api, params = {}) => defer(() => {
      if (ctrl.readOnly) throw new Error('Read-only is the sole option');
      return (resource as any).saveMany(items, params);
    }).pipe(takeUntil(stopped));
    const failBulk = (error, target = ctrl) => {
      target.form = target.form || {};
      target.form.apiErrors = bulkApiErrorsFromResponse(error);
      target.form.bulkError = true;
      state.bulkUndoError.set(true);
    };
    ctrl.miscBulkAction = (modal, modalCtrl, items, resource?, params?) => {
      if (ctrl.readOnly || state.readOnly() || state.bulkSaving()) return;
      const target = modalCtrl || ctrl;
      target.form = target.form || {};
      target.form.apiErrors = [];
      target.form.bulkError = false;
      if (!items.length) { modal?.close(); return; }
      const originals = Object.values(state.selected()).map((item) => _.pick(item, Object.keys(items[0])));
      state.bulkSaving.set(true);
      this.modalLoading.applyModalLoading();
      bulkSave(items, resource || api, params).pipe(finalize(() => {
        state.bulkSaving.set(false);
        this.modalLoading.removeModalLoading();
      })).subscribe({
        next: () => {
          ctrl.bulkParams = params;
          if (!resource) state.undoBulkSelection.set(originals);
          target.form.bulkError = false;
          modal?.close({items});
          state.load();
          config.afterBulkAction?.();
        }, error: (error) => failBulk(error, target)
      });
    };
    ctrl.undoMiscBulkAction = () => {
      if (ctrl.readOnly || state.readOnly() || state.bulkSaving() || !state.undoBulkSelection().length) return;
      state.bulkSaving.set(true);
      bulkSave(state.undoBulkSelection(), api, ctrl.bulkParams).pipe(finalize(() => state.bulkSaving.set(false))).subscribe({
        next: () => { state.undoBulkSelection.set([]); state.load(); config.afterBulkAction?.(); },
        error: (error) => failBulk(error)
      });
    };
    const doBulk = (items, action, param, success) => {
      if (ctrl.readOnly || state.readOnly() || state.bulkSaving() || !Object.keys(items).length) return;
      const payload = Object.values(items).map((item: any) => ({id: item.id, [param]: action || item[param]}));
      state.bulkSaving.set(true);
      bulkSave(payload, api, ctrl.bulkParams).pipe(finalize(() => state.bulkSaving.set(false))).subscribe({
        next: () => { success(); state.load(); config.afterBulkAction?.(); config.afterAction?.(items, action); },
        error: (error) => failBulk(error)
      });
    };
    ctrl.doBulkAction = (action, param = 'objectState') => {
      const selected = state.selected();
      const undo = Object.values(selected).map((item) => _.pick(item, ['id', param]));
      doBulk(selected, action, param, () => {
        ctrl.prevParam = param;
        state.undoAction.set(action);
        state.undoItems.set(undo as any);
      });
    };
    ctrl.undoBulkAction = () => doBulk(state.undoItems(), null, ctrl.prevParam || 'objectState', () => {
      state.undoItems.set([]); state.undoAction.set(null);
    });
    const dispose = () => {
      stopped.next(); stopped.complete(); state.destroy(); unregister?.();
    };
    const unregister = destroyRef.onDestroy(dispose);
    ctrl.disposeList = dispose;
    init();
    if (config.noInitQuery) state.loading.set(false);
    else state.load();
    return ctrl;
  }
}
